import DOMPurify from "dompurify";

interface RichTextPreviewProps {
  content: string;
  className?: string;
}

// Convert YouTube links and iframes into responsive embeds and normalize markup
function processYouTubeContent(content: string): string {
  if (!content) return content;
  let processedContent = content.replace(
    /<a[^>]*href="(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+))"[^>]*>.*?<\/a>/g,
    (_match, _url, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  processedContent = processedContent.replace(
    /(?<!href=["'])https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?!["'])/g,
    (_match, videoId) => {
      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      return `<div class="youtube-embed-wrapper"><iframe width="100%" height="315" src="${embedUrl}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
  );

  processedContent = processedContent.replace(
    /<iframe([^>]*youtube[^>]*)><\/iframe>/g,
    '<div class="youtube-embed-wrapper"><iframe$1></iframe></div>'
  );

  return processedContent;
}

const styles = `
.rich-text-prose p { margin: 0.5em 0; line-height: 1.7; }
.rich-text-prose ul, .rich-text-prose ol { padding-left: 1.5em; margin: 0.5em 0; }
.rich-text-prose li { margin: 0.25em 0; }
.rich-text-prose a { color: rgb(var(--brand-600)); text-decoration: underline; }
.rich-text-prose blockquote { border-left: 4px solid hsl(var(--border)); padding-left: 1em; margin: 1em 0; font-style: italic; color: hsl(var(--muted-foreground)); }
.rich-text-prose code { background-color: hsl(var(--muted)); padding: 0.2em 0.4em; border-radius: 0.25em; font-family: 'Courier New', monospace; font-size: 0.9em; }
.rich-text-prose pre { background-color: hsl(var(--muted)); padding: 1em; border-radius: 0.5em; overflow-x: auto; margin: 1em 0; }
.youtube-embed-wrapper { position: relative; width: 100%; margin: 1em 0; }
.youtube-embed-wrapper iframe { aspect-ratio: 16 / 9; width: 100%; height: auto; border-radius: 0.5rem; box-shadow: 0 1px 10px rgba(0,0,0,0.08); }
@supports not (aspect-ratio: 16 / 9) { .youtube-embed-wrapper { padding-top: 56.25%; } .youtube-embed-wrapper iframe { position: absolute; top: 0; left: 0; height: 100%; } }
`;

export function RichTextPreview({
  content,
  className = "",
}: RichTextPreviewProps) {
  // First process YouTube embeds, then sanitize
  const processed = processYouTubeContent(content || "");
  const sanitized = DOMPurify.sanitize(processed, {
    ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "referrerpolicy"],
    ADD_TAGS: ["iframe", "div"],
  });

  return (
    <div className={`rich-text-preview ${className}`}>
      <style>{styles}</style>
      <div
        className="prose max-w-none text-foreground/90 rich-text-prose"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    </div>
  );
}
